import { Lightbulb, ArrowLeft } from "lucide-react";

/**
 * SuggestionsCard — shared between the text-search results page (Home)
 * and the OCR modal. Renders one row per suggestion with:
 *   • corrected_name (headline)  ← alias that scored highest
 *   • canonical name             ← "الاسم المعتمد"
 *   • similarity_percent / difference_percent (real numbers from server)
 *
 * `onPick(suggestion)` receives the full object. Callers open the drug
 * card by drug_id directly — no re-search, no Gemini/DeepSeek.
 */
export default function SuggestionsCard({ query, suggestions, onPick }) {
  if (!suggestions || suggestions.length === 0) return null;
  return (
    <div
      data-testid="suggestions-card"
      className="fade-up bg-amber-50 border-2 border-amber-400 rounded-2xl p-6 sm:p-7 w-full max-w-3xl mx-auto text-right mb-6"
    >
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 shrink-0 rounded-xl bg-amber-500 text-white flex items-center justify-center">
          <Lightbulb className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          {query && (
            <div className="text-sm font-bold tracking-tight text-amber-800 mb-1">
              لم نعثر على تطابق دقيق لـ «{query}»
            </div>
          )}
          <h3 className="text-lg sm:text-xl font-bold text-amber-900 mb-4">
            هل تقصد أحد هذه الأدوية؟
          </h3>
          <div className="flex flex-col gap-3">
            {suggestions.map((s) => {
              // Server always emits both, but fall back for legacy rows.
              const corrected = s.corrected_name || s.name;
              const pct       = Number.isFinite(s.similarity_percent)
                ? s.similarity_percent
                : Math.round((s.similarity || 0) * 100);
              const diff      = Number.isFinite(s.difference_percent)
                ? s.difference_percent
                : 100 - pct;
              // corrected differs from canonical when the alias won.
              const showsAlias = corrected && corrected !== s.name;
              return (
                <button
                  key={s.id}
                  type="button"
                  data-testid={`suggestion-${s.id}`}
                  onClick={() => onPick(s)}
                  className="text-right bg-white hover:bg-amber-100 border-2 border-amber-300 hover:border-amber-600 rounded-xl px-4 py-3 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <ArrowLeft className="w-4 h-4 text-amber-700 shrink-0" />
                    <span data-testid={`suggestion-corrected-${s.id}`}
                          className="font-bold text-slate-900">
                      هل تقصد: {corrected}؟
                    </span>
                  </div>
                  {showsAlias && (
                    <div data-testid={`suggestion-canonical-${s.id}`}
                         className="text-xs text-slate-600 mb-1">
                      الاسم المعتمد: <b>{s.name}</b>
                      {s.name_en && (
                        <span className="text-slate-400" dir="ltr">
                          {" "}({s.name_en})
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
                    <span>
                      نسبة التشابه الكتابي:{" "}
                      <b data-testid={`suggestion-similarity-${s.id}`}>{pct}٪</b>
                    </span>
                    <span>
                      نسبة الاختلاف الكتابي:{" "}
                      <b data-testid={`suggestion-difference-${s.id}`}>{diff}٪</b>
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
